import {
  Alert,
  Header,
  Modal,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDlqTable } from "../../hooks/useDlqTable";
import { DlqTable } from "./DlqTable";
import { QueueSummary } from "./QueueSummary";
import { JsonModal } from "../common/JsonModal";
import { ErrorModal } from "./ErrorModal";
import { DLQItem, Stage } from "../../lusca-search-api/generated-src";
import { decompressAndDeserializeData } from "../../helpers/compressionUtils";
import { setModalParam, readInitialModal } from "../../helpers/modalUrlUtils";

interface DlqDeepDiveModalProps {
  visible: boolean;
  onDismiss: () => void;
  dlqItems: DLQItem[];
  stage: Stage;
}

export default function DlqDeepDiveModal({
  visible,
  onDismiss,
  dlqItems,
  stage,
}: DlqDeepDiveModalProps) {
  const {
    selectedItem,
    setSelectedItem,
    sortingColumn,
    sortingDescending,
    filteringText,
    setFilteringText,
    currentPageIndex,
    setCurrentPageIndex,
    pageSize,
    queueSummary,
    filteredItems,
    paginatedItems,
    handleSortingChange,
  } = useDlqTable(dlqItems);

  const [selectedErrorItem, setSelectedErrorItem] = useState<DLQItem | null>(
    null
  );
  const [staleDeepLink, setStaleDeepLink] = useState(false);

  // Rehydrate the JSON/error modal once from a ?modal=dlq-json|dlq-error deep
  // link, after the async DLQ items have loaded.
  const hasRehydrated = useRef(false);
  useEffect(() => {
    if (hasRehydrated.current) return;
    const { modal, dlqItemPk } = readInitialModal();
    if ((modal !== "dlq-json" && modal !== "dlq-error") || !dlqItemPk) {
      hasRehydrated.current = true;
      return;
    }
    if (dlqItems.length === 0) return;
    hasRehydrated.current = true;
    const match = dlqItems.find((item) => item.pk === dlqItemPk);
    if (!match) {
      setStaleDeepLink(true);
      setModalParam("dlq");
      return;
    }
    if (modal === "dlq-json") setSelectedItem(match);
    else setSelectedErrorItem(match);
  }, [dlqItems, setSelectedItem]);

  const openJsonModal = (item: DLQItem) => {
    setSelectedItem(item);
    setModalParam("dlq-json", { dlqItemPk: item.pk });
  };

  const closeJsonModal = () => {
    setSelectedItem(null);
    setModalParam("dlq");
  };

  const openErrorModal = (item: DLQItem) => {
    setSelectedErrorItem(item);
    setModalParam("dlq-error", { dlqItemPk: item.pk });
  };

  const closeErrorModal = () => {
    setSelectedErrorItem(null);
    setModalParam("dlq");
  };

  const processedContent = useMemo(() => {
    if (!selectedItem) return null;
    if (selectedItem.is_malformed_json) {
      return selectedItem.processed_body;
    }
    if (typeof selectedItem.processed_body === "string") {
      try {
        return decompressAndDeserializeData(selectedItem.processed_body);
      } catch (error) {
        console.error("Failed to decompress DLQ data:", error);
        return selectedItem.processed_body;
      }
    }
    return selectedItem.processed_body;
  }, [selectedItem]);

  return (
    <>
      <Modal
        visible={visible}
        onDismiss={onDismiss}
        size="max"
        header={<Header variant="h2">DLQ Deep Dive</Header>}
      >
        <SpaceBetween direction="vertical" size="l">
          {staleDeepLink && (
            <Alert
              type="info"
              dismissible
              onDismiss={() => setStaleDeepLink(false)}
              header="Linked item no longer in the DLQ"
            >
              The DLQ item from this link is no longer present — it may have
              been redriven, or aged out of the hourly-refreshed source. Showing
              the current DLQ contents instead.
            </Alert>
          )}
          <QueueSummary summary={queueSummary} />
          <DlqTable
            items={paginatedItems}
            allItems={filteredItems}
            sortingColumn={sortingColumn}
            sortingDescending={sortingDescending}
            onSortingChange={handleSortingChange}
            filteringText={filteringText}
            onFilteringChange={setFilteringText}
            currentPageIndex={currentPageIndex}
            onPageChange={setCurrentPageIndex}
            pageSize={pageSize}
            onItemSelect={openJsonModal}
            onErrorSelect={openErrorModal}
            filteredItemsCount={filteredItems.length}
            stage={stage}
          />
        </SpaceBetween>
      </Modal>

      {selectedItem && (
        <JsonModal
          title={selectedItem.is_malformed_json ? "Raw Message" : "Raw JSON"}
          content={processedContent}
          visible
          onDismiss={closeJsonModal}
          isMalformedJson={selectedItem.is_malformed_json}
        />
      )}

      {selectedErrorItem && (
        <ErrorModal
          visible
          onDismiss={closeErrorModal}
          errorType={selectedErrorItem.error_type}
          errorMessage={selectedErrorItem.error_message}
          stackTrace={selectedErrorItem.error_stack_trace}
        />
      )}
    </>
  );
}
